import { Sentry, createSentryErrorHandler, logError } from '@/lib/sentry.lib';

import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { useShallow } from 'zustand/shallow';

import { useAppStore } from '@/store/app.store';
import { useCoworkerProfileStore } from '@/store/coworker-preferences.store';
import { initOrdersExternalApi } from '@/store/orders-external-api.store';
import { onlineManager } from '@tanstack/react-query';

import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import { App } from '@/components/app/app.component';
import { LoadingPage } from '@/components/loading';

import { bootstrapApplication } from '@/utils/app.utils';

import { useNavigatorOnLine } from './components/online-update/online-update.hooks';

export const Main = () => {
    const isOnline = useNavigatorOnLine();
    const bootstrapResult = useAppStore((state) => state.bootstrapResult);
    const setBootstrapResult = useAppStore((state) => state.setBootstrapResult);
    const { setCoworkerProfile, msalProfile } = useCoworkerProfileStore(
        useShallow((state) => ({
            setCoworkerProfile: state.setCoworkerProfile,
            msalProfile: state.msalProfile,
        }))
    );

    useEffect(() => {
        bootstrapApplication()
            .then((result) => {
                setBootstrapResult(result);
                setCoworkerProfile();
                initOrdersExternalApi();

                if (!result) {
                    logTelemetry('error', TELEMETRY_LOGS.AppBootstrap, {
                        bootstrap: {
                            completed: false,
                        },
                    });
                }
            })
            .catch((error: unknown) => {
                logTelemetry('error', TELEMETRY_LOGS.AppBootstrap, {
                    bootstrap: {
                        completed: false,
                    },
                });
                logError(error);
            });
    }, [setBootstrapResult, setCoworkerProfile]);

    useEffect(() => {
        onlineManager.setOnline(isOnline);

        if (!isOnline) {
            logTelemetry('warn', TELEMETRY_LOGS.ConnectionStatusChanged, {
                connection: {
                    status: 'offline',
                },
            });
        }
    }, [isOnline]);

    useEffect(() => {
        if (msalProfile) {
            Sentry.setUser({ id: msalProfile.id });
        }
    }, [msalProfile]);

    // The app needs the bootstrap config (API base URLs) before the router and
    // its data-fetching can run. The buCode gate lives in RootLayout, where
    // React Query is available.
    if (!bootstrapResult) {
        return <LoadingPage />;
    }

    return <App />;
};

const rootElement = document.getElementById('app');
if (rootElement) {
    const root = createRoot(rootElement, {
        // Callback called when an error is thrown and not caught by an ErrorBoundary.
        onUncaughtError: createSentryErrorHandler((error, errorInfo) => {
            if (import.meta.env.VITE_ENV === 'local') {
                // oxlint-disable-next-line no-console
                console.warn('Uncaught error', error, errorInfo.componentStack);
            }
        }),
        // Callback called when React catches an error in an ErrorBoundary.
        onCaughtError: createSentryErrorHandler(),
        // Callback called when React automatically recovers from errors.
        onRecoverableError: createSentryErrorHandler(),
    });
    root.render(<Main />);
} else if (import.meta.env.MODE !== 'test') {
    throw new Error('Failed to find the root element');
}
